import { db, getDeviceId } from "../db/database";
import type {
  AppSettings,
  AuditLog,
  InventoryBalance,
  InventoryMovement,
  InventoryRow,
  MovementType,
  PendingSync,
  Product,
  ProductFormInput,
  ProductVariant,
  WarehouseCode,
} from "../types";
import { getSession } from "./auth";
import { nextBalance, transferBalances } from "./inventoryMath.mjs";
import { randomId } from "../utils/id";

const iso = () => new Date().toISOString();
const uuid = randomId;

async function actor() {
  const session = await getSession();
  return session?.user ?? { id: "offline-user", name: "Usuário offline" };
}

async function queue(operationType: PendingSync["operationType"], payload: Record<string, unknown>, operationId?: string) {
  const user = await actor();
  const deviceId = await getDeviceId();
  const timestamp = iso();
  await db.pendingSync.put({
    id: operationId ?? uuid("sync"), operationType, payload, deviceId, userId: user.id,
    status: "pendente", attempts: 0, createdAt: timestamp, updatedAt: timestamp,
  });
}

async function audit(action: string, entityType: string, entityId: string, before?: unknown, after?: unknown) {
  const user = await actor();
  const entry: AuditLog = {
    id: uuid("audit"), action, entityType, entityId, before, after,
    userId: user.id, userName: user.name, deviceId: await getDeviceId(), createdAt: iso(),
  };
  await db.auditLogs.put(entry);
}

export async function getInventoryRows(): Promise<InventoryRow[]> {
  const [products, variants, balances] = await Promise.all([
    db.products.toArray(), db.variants.toArray(), db.balances.toArray(),
  ]);
  const productsById = new Map(products.map((item) => [item.id, item]));
  const balanceKey = new Map(balances.map((item) => [`${item.variantId}:${item.warehouse}`, item]));
  return variants.reduce<InventoryRow[]>((result, variant) => {
      const product = productsById.get(variant.productId);
      const galpao = balanceKey.get(`${variant.id}:galpao`);
      const malharia = balanceKey.get(`${variant.id}:malharia`);
      if (product && galpao) result.push({ product, variant, galpao, ...(malharia ? { malharia } : {}), total: galpao.quantity + (variant.malhariaEnabled ? malharia?.quantity ?? 0 : 0) });
      return result;
    }, []);
}

export async function createProduct(input: ProductFormInput): Promise<Product> {
  const timestamp = iso();
  const product: Product = {
    id: uuid("product"), name: input.name.trim(), sku: input.sku.trim().toUpperCase(),
    category: input.category.trim(), description: input.description?.trim(), notes: input.notes?.trim(),
    status: input.status, createdAt: timestamp, updatedAt: timestamp,
  };
  if (!product.name || !product.sku || input.variants.length === 0) throw new Error("Informe produto, SKU e pelo menos uma cor.");
  if (await db.products.where("sku").equalsIgnoreCase(product.sku).first()) throw new Error("Já existe um produto com este SKU.");

  const variants: ProductVariant[] = [];
  const balances: InventoryBalance[] = [];
  const movements: InventoryMovement[] = [];
  const user = await actor();
  for (const item of input.variants) {
    if (!item.colorName.trim()) throw new Error("Todas as variações precisam de uma cor.");
    const variantId = uuid("variant");
    variants.push({
      id: variantId, productId: product.id, colorName: item.colorName.trim(), colorCode: item.colorCode,
      minimumStock: Math.max(0, item.minimumStock), malhariaEnabled: item.malhariaEnabled,
      status: "ativo", updatedAt: timestamp,
    });
    const locations: Array<[WarehouseCode, number]> = [["galpao", Number(item.galpao) || 0]];
    if (item.malhariaEnabled) locations.push(["malharia", Number(item.malharia) || 0]);
    for (const [warehouse, quantity] of locations) {
      balances.push({ id: `${variantId}:${warehouse}`, variantId, warehouse, quantity, version: 1, updatedAt: timestamp });
      if (quantity !== 0) movements.push({
        id: uuid("movement"), operationId: uuid("initial"), variantId, warehouse, type: "estoque_inicial", quantity,
        before: 0, after: quantity, userId: user.id, userName: user.name, source: "manual",
        note: "Estoque inicial do cadastro", createdAt: timestamp, syncStatus: "pendente",
      });
    }
  }

  await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.pendingSync, db.auditLogs, db.sessions, db.kv], async () => {
    await db.products.add(product);
    await db.variants.bulkAdd(variants);
    await db.balances.bulkAdd(balances);
    if (movements.length) await db.movements.bulkAdd(movements);
    await queue("product_upsert", { product, variants, balances, movements });
    await audit("Produto cadastrado", "product", product.id, undefined, { product, variants, balances });
  });
  return product;
}

export interface MovementInput {
  variantId: string;
  warehouse: WarehouseCode;
  type: MovementType;
  quantity?: number;
  setTo?: number;
  note?: string;
  source?: InventoryMovement["source"];
  sourceFileId?: string;
}

export async function applyMovement(input: MovementInput): Promise<InventoryMovement> {
  const settings = await db.settings.get("app");
  const currentUser = await actor();
  const operationId = uuid("op");
  let created!: InventoryMovement;

  await db.transaction("rw", [db.balances, db.variants, db.movements, db.pendingSync, db.auditLogs, db.sessions, db.kv], async () => {
    const variant = await db.variants.get(input.variantId);
    if (!variant) throw new Error("Variação não encontrada.");
    if (input.warehouse === "malharia" && !variant.malhariaEnabled) throw new Error("O estoque da malharia está desativado para esta cor.");
    const balanceId = `${input.variantId}:${input.warehouse}`;
    const balance = (await db.balances.get(balanceId)) ?? { id: balanceId, variantId: input.variantId, warehouse: input.warehouse, quantity: 0, version: 0, updatedAt: iso() };
    const before = balance.quantity;
    const delta = input.setTo !== undefined ? Number(input.setTo) - before : Number(input.quantity ?? 0);
    if (!Number.isFinite(delta) || delta === 0) throw new Error("Informe uma quantidade diferente de zero.");
    const after = nextBalance(before, delta, settings?.allowNegativeStock ?? false);
    const timestamp = iso();
    const updated: InventoryBalance = { ...balance, quantity: after, version: balance.version + 1, updatedAt: timestamp };
    created = {
      id: uuid("movement"), operationId, variantId: input.variantId, warehouse: input.warehouse,
      type: input.type, quantity: delta, before, after, userId: currentUser.id, userName: currentUser.name,
      source: input.source ?? "manual", note: input.note?.trim(), sourceFileId: input.sourceFileId,
      createdAt: timestamp, syncStatus: "pendente",
    };
    await db.balances.put(updated);
    await db.movements.add(created);
    await queue("movement", { movement: created, expectedVersion: balance.version }, operationId);
    await audit("Movimentação de estoque", "variant", input.variantId, balance, updated);
  });
  return created;
}

export interface BulkStockEdit {
  variantId: string;
  galpao: number;
  malhariaEnabled: boolean;
  malharia: number;
}

export async function applyBulkStockEdits(edits: BulkStockEdit[], note?: string): Promise<{ movements: number; variantsChanged: number }> {
  if (!edits.length) throw new Error("Selecione pelo menos uma cor para editar.");
  if (new Set(edits.map((edit) => edit.variantId)).size !== edits.length) throw new Error("Há cores repetidas na seleção.");
  const settings = await db.settings.get("app");
  const currentUser = await actor();
  const deviceId = await getDeviceId();
  const baseTime = Date.now();
  const operationNote = note?.trim() || "Edição de estoque em massa";
  let movementCount = 0;
  let variantsChanged = 0;

  await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.pendingSync, db.auditLogs], async () => {
    const updatedVariantsByProduct = new Map<string, ProductVariant[]>();
    const createdBalancesByProduct = new Map<string, InventoryBalance[]>();
    const movements: InventoryMovement[] = [];
    const movementOperations: PendingSync[] = [];
    const audits: AuditLog[] = [];
    let sequence = 0;

    for (const edit of edits) {
      const galpao = Number(edit.galpao);
      const malharia = Number(edit.malharia);
      if (!Number.isInteger(galpao) || !Number.isInteger(malharia)) throw new Error("Informe quantidades inteiras para todos os estoques.");
      if (!(settings?.allowNegativeStock ?? false) && (galpao < 0 || (edit.malhariaEnabled && malharia < 0))) {
        throw new Error("Estoque negativo está desativado nas configurações.");
      }

      const variant = await db.variants.get(edit.variantId);
      if (!variant) throw new Error("Uma das cores selecionadas não foi encontrada.");
      const product = await db.products.get(variant.productId);
      if (!product) throw new Error("O produto de uma das cores selecionadas não foi encontrado.");
      const currentMalharia = await db.balances.get(`${variant.id}:malharia`);
      if (!edit.malhariaEnabled && (malharia !== 0 || (currentMalharia?.quantity ?? 0) !== 0)) {
        throw new Error(`Zere o estoque da malharia de ${product.sku} / ${variant.colorName} antes de desativá-lo.`);
      }

      if (variant.malhariaEnabled !== edit.malhariaEnabled) {
        const updatedVariant = { ...variant, malhariaEnabled: edit.malhariaEnabled, updatedAt: new Date(baseTime + sequence++).toISOString() };
        await db.variants.put(updatedVariant);
        const list = updatedVariantsByProduct.get(product.id) ?? [];
        list.push(updatedVariant);
        updatedVariantsByProduct.set(product.id, list);
        variantsChanged += 1;
        audits.push({
          id: uuid("audit"), action: edit.malhariaEnabled ? "Estoque da malharia ativado" : "Estoque da malharia desativado",
          entityType: "variant", entityId: variant.id, before: variant, after: updatedVariant,
          userId: currentUser.id, userName: currentUser.name, deviceId, createdAt: updatedVariant.updatedAt,
        });
      }

      const targets: Array<[WarehouseCode, number]> = [["galpao", galpao]];
      if (edit.malhariaEnabled) targets.push(["malharia", malharia]);
      for (const [warehouse, target] of targets) {
        const balanceId = `${variant.id}:${warehouse}`;
        const stored = await db.balances.get(balanceId);
        const balance: InventoryBalance = stored ?? {
          id: balanceId, variantId: variant.id, warehouse, quantity: 0, version: 0,
          updatedAt: new Date(baseTime + sequence++).toISOString(),
        };
        if (!stored && warehouse === "malharia") {
          const list = createdBalancesByProduct.get(product.id) ?? [];
          list.push(balance);
          createdBalancesByProduct.set(product.id, list);
          await db.balances.put(balance);
        }
        const delta = target - balance.quantity;
        if (delta === 0) continue;
        const after = nextBalance(balance.quantity, delta, settings?.allowNegativeStock ?? false);
        const createdAt = new Date(baseTime + sequence++).toISOString();
        const updatedBalance: InventoryBalance = { ...balance, quantity: after, version: balance.version + 1, updatedAt: createdAt };
        const operationId = uuid("bulk");
        const movement: InventoryMovement = {
          id: uuid("movement"), operationId, variantId: variant.id, warehouse, type: "alteracao_manual",
          quantity: delta, before: balance.quantity, after, userId: currentUser.id, userName: currentUser.name,
          source: "manual", note: operationNote, createdAt, syncStatus: "pendente",
        };
        await db.balances.put(updatedBalance);
        movements.push(movement);
        movementOperations.push({
          id: operationId, operationType: "movement", payload: { movement, expectedVersion: balance.version },
          deviceId, userId: currentUser.id, status: "pendente", attempts: 0, createdAt, updatedAt: createdAt,
        });
        audits.push({
          id: uuid("audit"), action: "Estoque alterado em massa", entityType: "variant", entityId: variant.id,
          before: balance, after: updatedBalance, userId: currentUser.id, userName: currentUser.name, deviceId, createdAt,
        });
      }
    }

    const productOperations: PendingSync[] = [];
    for (const [productId, variants] of updatedVariantsByProduct) {
      const product = await db.products.get(productId);
      if (!product) continue;
      const createdAt = new Date(baseTime - 1_000).toISOString();
      productOperations.push({
        id: uuid("sync"), operationType: "product_upsert",
        payload: { product, variants, balances: createdBalancesByProduct.get(productId) ?? [] },
        deviceId, userId: currentUser.id, status: "pendente", attempts: 0, createdAt, updatedAt: createdAt,
      });
    }
    if (movements.length) await db.movements.bulkAdd(movements);
    if (productOperations.length || movementOperations.length) await db.pendingSync.bulkAdd([...productOperations, ...movementOperations]);
    if (audits.length) await db.auditLogs.bulkAdd(audits);
    movementCount = movements.length;
  });
  return { movements: movementCount, variantsChanged };
}

export async function enableMalhariaStock(variantId: string): Promise<void> {
  const [variant, galpao, malharia] = await Promise.all([
    db.variants.get(variantId),
    db.balances.get(`${variantId}:galpao`),
    db.balances.get(`${variantId}:malharia`),
  ]);
  if (!variant) throw new Error("Variação não encontrada.");
  if (variant.malhariaEnabled) return;
  await applyBulkStockEdits([{
    variantId,
    galpao: galpao?.quantity ?? 0,
    malhariaEnabled: true,
    malharia: malharia?.quantity ?? 0,
  }], "Ativação do estoque da malharia");
}

export async function transferStock(variantId: string, from: WarehouseCode, quantity: number, note?: string): Promise<void> {
  if (quantity <= 0 || !Number.isFinite(quantity)) throw new Error("Informe uma quantidade válida para transferir.");
  const to: WarehouseCode = from === "galpao" ? "malharia" : "galpao";
  const settings = await db.settings.get("app");
  const currentUser = await actor();
  const operationId = uuid("transfer");

  await db.transaction("rw", [db.balances, db.variants, db.movements, db.pendingSync, db.auditLogs, db.sessions, db.kv], async () => {
    const variant = await db.variants.get(variantId);
    if (!variant?.malhariaEnabled) throw new Error("Ative o estoque da malharia antes de realizar transferências.");
    const source = await db.balances.get(`${variantId}:${from}`);
    const destination = await db.balances.get(`${variantId}:${to}`);
    if (!source || !destination) throw new Error("Saldos da variação não encontrados.");
    const calculated = transferBalances(source.quantity, destination.quantity, quantity, settings?.allowNegativeStock ?? false);
    const timestamp = iso();
    const updatedSource = { ...source, quantity: calculated.source, version: source.version + 1, updatedAt: timestamp };
    const updatedDestination = { ...destination, quantity: calculated.destination, version: destination.version + 1, updatedAt: timestamp };
    const outgoing: InventoryMovement = {
      id: uuid("movement"), operationId, variantId, warehouse: from, type: "transferencia_saida", quantity: -quantity,
      before: source.quantity, after: updatedSource.quantity, userId: currentUser.id, userName: currentUser.name,
      source: "manual", note, createdAt: timestamp, syncStatus: "pendente",
    };
    const incoming: InventoryMovement = {
      id: uuid("movement"), operationId, variantId, warehouse: to, type: "transferencia_entrada", quantity,
      before: destination.quantity, after: updatedDestination.quantity, userId: currentUser.id, userName: currentUser.name,
      source: "manual", note, createdAt: timestamp, syncStatus: "pendente",
    };
    await db.balances.bulkPut([updatedSource, updatedDestination]);
    await db.movements.bulkAdd([outgoing, incoming]);
    await queue("transfer", { variantId, from, to, quantity, note, movements: [outgoing, incoming], expectedVersions: { [from]: source.version, [to]: destination.version } }, operationId);
    await audit("Transferência entre depósitos", "variant", variantId, { source, destination }, { source: updatedSource, destination: updatedDestination });
  });
}

export async function archiveProduct(productId: string): Promise<void> {
  const before = await db.products.get(productId);
  if (!before) return;
  const after = { ...before, status: "arquivado" as const, updatedAt: iso() };
  await db.transaction("rw", [db.products, db.pendingSync, db.auditLogs, db.sessions, db.kv], async () => {
    await db.products.put(after);
    await queue("product_upsert", { product: after });
    await audit("Produto arquivado", "product", productId, before, after);
  });
}

export async function saveSettings(changes: Partial<AppSettings>): Promise<void> {
  const before = await db.settings.get("app");
  if (!before) return;
  const after = { ...before, ...changes, id: "app" as const };
  await db.transaction("rw", [db.settings, db.pendingSync, db.auditLogs, db.sessions, db.kv], async () => {
    await db.settings.put(after);
    await queue("settings", { settings: after });
    await audit("Configurações alteradas", "settings", "app", before, after);
  });
}
